import gsap from 'gsap'
import { getLenis, startLenis, stopLenis } from './lenis'
import { getMap } from './map'

type ConstellationState = 'INTRO' | 'DISCOVERY' | 'PLACING' | 'COMPLETE' | 'TRANSITION_OUT'

type SlotId = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11'
type SpecialStarShape = 'circle' | 'petal40' | 'burst57' | 'chunk74'

interface TargetSlot {
  id: SlotId
  x: number
  y: number
}

interface SpecialStarConfig {
  id: SlotId
  target: SlotId
  memory: string
}

interface SpecialStar {
  id: SlotId
  targetId: SlotId
  memory: string
  shape: SpecialStarShape
  nx: number
  ny: number
  baseRadius: number
  pulseOffset: number
  glowBoost: number
  placed: boolean
  placementIndex: number | null
  assignedHue: number
  placedAtMs: number | null
  targetTrailOpacity: number
}

interface BackgroundStar {
  nx: number
  ny: number
  radius: number
  twinkleSpeed: number
  twinkleOffset: number
}

interface TrailParticle {
  nx: number
  ny: number
  life: number
  size: number
}

interface ShootingStar {
  nx: number
  ny: number
  vnx: number
  vny: number
  age: number
  lifetime: number
  length: number
}

interface PlacedTrailSegment {
  x1: number
  y1: number
  x2: number
  y2: number
  hue: number
  alpha: number
}

interface MemoryChip {
  id: SlotId
  text: string
}

interface SceneTweens {
  backgroundDim: number
  silhouetteOpacity: number
  silhouetteScale: number
  constellationGlow: number
  lineDarken: number
  flash: number
  sceneFade: number
}

interface InitConstellationOptions {
  onConstellationComplete?: () => void
}

const IS_CONSTELLATION_DEBUG =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('constellation-debug')

const TARGET_SLOTS_DEFAULT: TargetSlot[] = [
  { id: '1', x: 0.32, y: 0.36 },
  { id: '2', x: 0.45, y: 0.32 },
  { id: '3', x: 0.61, y: 0.37 },
  { id: '4', x: 0.75, y: 0.45 },
  { id: '5', x: 0.62, y: 0.6 },
  { id: '6', x: 0.65, y: 0.79 },
  { id: '7', x: 0.49, y: 0.57 },
  { id: '8', x: 0.31, y: 0.6 },
  { id: '9', x: 0.23, y: 0.76 },
  { id: '10', x: 0.32, y: 0.8 },
  { id: '11', x: 0.48, y: 0.8 },
]

const TARGET_SLOTS_IPAD_LANDSCAPE: TargetSlot[] = [
  { id: '1', x: 0.27, y: 0.42 },
  { id: '2', x: 0.46, y: 0.36 },
  { id: '3', x: 0.65, y: 0.42 },
  { id: '4', x: 0.81, y: 0.49 },
  { id: '5', x: 0.65, y: 0.66 },
  { id: '6', x: 0.68, y: 0.81 },
  { id: '7', x: 0.49, y: 0.59 },
  { id: '8', x: 0.26, y: 0.66 },
  { id: '9', x: 0.15, y: 0.74 },
  { id: '10', x: 0.25, y: 0.81 },
  { id: '11', x: 0.44, y: 0.86 },
]

const TARGET_SLOTS_IPAD_SMALL_LANDSCAPE: TargetSlot[] = [
  { id: '1', x: 0.26, y: 0.4 },
  { id: '2', x: 0.44, y: 0.35 },
  { id: '3', x: 0.66, y: 0.4 },
  { id: '4', x: 0.84, y: 0.49 },
  { id: '5', x: 0.63, y: 0.64 },
  { id: '6', x: 0.68, y: 0.79 },
  { id: '7', x: 0.48, y: 0.69 },
  { id: '8', x: 0.26, y: 0.62 },
  { id: '9', x: 0.13, y: 0.74 },
  { id: '10', x: 0.23, y: 0.81 },
  { id: '11', x: 0.45, y: 0.88 },
]

const LINE_CONNECTIONS: Array<[SlotId, SlotId]> = [
  ['1', '2'],
  ['2', '3'],
  ['3', '4'],
  ['4', '5'],
  ['5', '6'],
  ['5', '7'],
  ['7', '8'],
  ['8', '9'],
  ['8', '10'],
  ['7', '11'],
]

const COLLAPSE_TARGET_ID: SlotId = '11'

const SPECIAL_STARS: SpecialStarConfig[] = [
  { id: '1', target: '1', memory: 'A tattoo call that was supposed to be nothing. It was everything.' },
  { id: '2', target: '2', memory: 'You said I love you and I already knew.' },
  { id: '3', target: '3', memory: 'Late night calls where neither of us wanted to hang up.' },
  { id: '4', target: '4', memory: "I've been waiting for you my whole life without knowing it. A little longer is nothing." },
  { id: '5', target: '5', memory: 'You are the best thing that showed up unexpectedly.' },
  { id: '6', target: '6', memory: 'I stopped thinking of this as a possibility. It\'s a when, not an if.' },
  { id: '7', target: '7', memory: 'Fresh out the slammer, I know who your first call will be to.' },
  { id: '8', target: '8', memory: 'Games. Songs. Movies. The small ways our days overlapped.' },
  { id: '9', target: '9', memory: 'You said I\'m yours. I already was.' },
  { id: '10', target: '10', memory: 'One in a million. I mean that.' },
  { id: '11', target: '11', memory: 'You\'re worth every single second' },  
]

const SPECIAL_STAR_SHAPES_BY_ID: Record<SlotId, SpecialStarShape> = {
  '1': 'circle',
  '2': 'circle',
  '3': 'circle',
  '4': 'petal40',
  '5': 'petal40',
  '6': 'burst57',
  '7': 'chunk74',
  '8': 'burst57',
  '9': 'circle',
  '10': 'petal40',
  '11': 'circle',
}

const MOBILE_BREAKPOINT = 768
const IPAD_SMALL_LANDSCAPE_MIN_WIDTH = 960
const IPAD_SMALL_LANDSCAPE_MAX_WIDTH = 1079
const IPAD_LANDSCAPE_MIN_WIDTH = 1080
const IPAD_LANDSCAPE_MAX_WIDTH = 1240
const MOBILE_HIT_RADIUS = 34
const DESKTOP_HIT_RADIUS = 22
const BG_STARS_MOBILE = 120
const BG_STARS_DESKTOP = 220
const SPAWN_MIN_Y_DEFAULT = 0.12
const SPAWN_TOP_PADDING_N = 0.035
const MEMORY_VISIBLE_MS = 2800
const SKIP_IDLE_MS = 10000
const ORDER_HUE_START = 198
const ORDER_HUE_END = 338
const LOCK_ALIGN_TOLERANCE_PX = 3
const LOCK_FALLBACK_MS = 950

const PETAL40_VIEW_W = 40
const PETAL40_VIEW_H = 50
const PETAL40_PATH = new Path2D(
  'M38.6766 26.9933C26.743 22.6674 22.493 12.8896 21.6082 0.978444C21.5404 0.0634441 20.109 -0.440215 19.8004 0.512685C15.8763 12.6306 11.6395 21.3828 0.836032 23.1066C-0.00646812 23.2412 -0.283592 24.5152 0.337008 25.0436C6.52351 30.3137 16.689 39.2572 16.4964 48.2728C16.4696 49.5249 18.9307 50.818 19.9574 47.2465C22.7196 37.638 34.3466 30.6283 38.7306 29.3871C40.1098 28.9966 39.5328 27.3036 38.6766 26.9933Z'
)

const BURST57_VIEW_W = 57
const BURST57_VIEW_H = 60
const BURST57_PATH = new Path2D(
  'M56.2662 37.2629C49.5807 34.2068 44.4851 32.0337 37.8035 28.6495C41.9878 25.0867 48.1814 20.26 52.1582 16.468C52.8491 15.8091 52.7187 14.8597 49.2898 16.2647C44.3486 18.2892 39.1118 20.0199 34.0293 21.7813C33.5498 15.3651 32.9425 6.67543 32.2813 0.800733C32.162 -0.260567 31.5982 -0.691571 30.9732 2.25923C29.6373 8.56783 29.2481 16.8618 27.6524 23.1087C24.2179 18.954 18.5933 12.2932 15.0757 8.20833C14.1484 7.13143 12.9895 7.58202 13.5061 8.96882C15.54 14.4295 18.7388 20.2338 22.3721 26.4694C16.4263 27.9713 7.25818 29.28 0.865276 30.4815C-0.303624 30.7012 -0.929613 32.2703 3.32109 32.6475C9.47029 33.1933 15.5592 33.8113 21.7181 33.6254C19.3238 40.984 16.8279 47.2064 14.2964 56.0496C13.999 57.0889 14.5918 57.6699 15.1997 56.7799C19.6853 50.214 25.3628 43.2627 29.3433 36.3634C31.1714 41.7019 34.4114 52.4071 36.8655 58.3842C37.2622 59.3506 39.1029 60.9761 38.4146 55.3709C37.6134 48.8423 35.9973 41.1279 34.8645 34.8193C41.7156 36.9458 48.6655 38.3832 55.8462 38.8831C56.7544 38.9463 57.1783 37.6799 56.2662 37.2629Z'
)

const CHUNK74_VIEW_W = 74
const CHUNK74_VIEW_H = 70
const CHUNK74_PATH = new Path2D(
  'M71.5184 31.179C64.0255 30.6364 43.048 33.0072 41.1786 24.0199C40.5072 20.9703 39.983 17.8943 39.6058 14.7922C38.9476 10.4416 38.3462 6.092 37.8067 1.7248C37.574 -0.157703 34.4158 -0.821009 34.1988 1.39069C33.7893 5.56269 33.2477 9.69863 32.4315 13.8129C31.8878 16.5534 31.329 23.9782 29.3351 25.9739C24.5612 30.7514 8.16609 26.5509 2.33189 25.6842C0.233289 25.3724 -0.968663 28.151 0.998637 29.2614C6.72394 32.4926 12.431 35.7492 18.1998 38.8954C21.5617 40.7289 22.9213 44.838 21.3932 48.349C19.5143 52.6647 17.7784 57.0458 16.0067 61.4123C15.4506 62.7831 17.1163 64.2963 18.2567 63.0387C21.7391 59.1996 25.3158 55.4501 28.8739 51.6827C31.181 49.2404 35.0687 49.2878 37.3329 51.7704C42.4757 57.4096 47.6709 62.9992 52.8692 68.589C54.2598 70.0846 56.7772 68.3996 56.1832 66.5468C54.1176 60.1025 51.8505 53.7243 49.2406 47.4855C48.0397 44.6147 49.6119 41.3278 52.6051 40.4753C59.1146 38.6215 65.6451 36.8378 72.1583 35.0024C74.4391 34.3595 73.608 31.33 71.5184 31.179Z'
)

const BEAR_VIEW_W = 236
const BEAR_VIEW_H = 140
const BEAR_SILHOUETTE_PATH = new Path2D(
  'M235.5 62.4084C235.359 62.0868 235.122 61.8723 234.847 61.7204C234.913 61.4981 234.881 61.2712 234.669 61.1088C227.414 55.5568 227.348 45.6324 223.843 37.8557C220.493 30.4287 212.408 23.3939 204.133 22.3422C203.137 20.7813 201.65 19.3614 200.411 18.4166C198.346 16.8447 196.232 16.1312 193.714 16.9445C192.753 17.249 191.909 17.7563 191.193 18.3864C191.156 18.2928 191.066 18.2189 190.885 18.1978C188.267 17.8975 185.525 18.4139 182.891 18.5005C179.455 18.6082 176.252 18.0162 172.957 17.2661C172.819 17.2315 172.686 17.2055 172.546 17.172C170.444 13.5251 165.473 10.5163 161.872 8.52319C157.714 6.21643 152.971 5.33511 148.258 5.35334C147.66 5.35737 146.666 5.94158 146.853 6.6731L146.881 6.8499C144.115 6.3884 141.364 5.94142 138.535 6.00496C138.084 6.01494 136.84 6.98166 137.403 7.49149C137.627 7.69038 137.858 7.86193 138.083 8.04784C127.992 9.9006 118.392 6.23958 108.63 3.63494C98.4222 0.911108 87.7899 1.60273 77.415 0.00504643C76.9256 -0.0718896 75.7304 0.756415 76.2347 1.31195C76.644 1.77282 76.9067 1.87494 77.0265 1.88001C76.9756 1.9065 76.8969 1.95941 76.8005 2.0748C72.4495 0.688306 67.9726 1.9762 63.8631 3.72254C63.5057 3.86956 62.5137 4.69315 63.1944 5.07343C63.5377 5.26348 63.8736 5.45326 64.2173 5.65064C56.3557 9.34243 48.9348 13.8866 41.2346 17.909C40.723 18.1758 39.9838 19.5648 41.1024 19.3934C41.5905 19.3153 42.0775 19.2589 42.5731 19.2027C36.5334 22.6675 31.7201 28.4347 28.2456 34.2855C24.211 41.0987 21.8669 50.0001 26.6083 56.943C26.5454 57.1162 26.4744 57.2888 26.4178 57.4626C25.4313 60.1772 24.8704 63.1422 23.9883 65.8832C22.8314 69.4903 21.5399 73.1004 20.722 76.8051C20.6106 77.3076 21.008 77.3873 21.4253 77.3211C18.7064 84.9485 8.89782 87.5862 5.70182 94.8486C5.65473 94.9586 5.65841 95.0362 5.67715 95.0933C5.57995 95.0753 5.46678 95.0995 5.3577 95.1658C5.32947 95.1774 5.30694 95.1969 5.27846 95.2252C-2.91449 102.572 -0.427535 118.976 5.01564 127.307C7.12319 130.531 12.4236 132.562 15.4248 129.257C17.4963 126.981 16.9641 123.942 14.6752 122.855C16.1421 119.738 17.6445 116.623 19.1672 113.536C20.4054 111.024 22.1135 109.892 24.5818 108.695C26.8001 107.615 31.6516 106.125 33.1149 103.444C32.5371 105.466 32.1199 107.531 31.9753 109.633C31.9295 110.362 32.7721 110.242 33.196 110.021C33.7783 109.723 34.3314 109.451 34.8775 109.172C34.009 111.252 33.3491 113.397 33.4279 115.692C33.4603 116.6 34.5136 116.293 34.9871 115.913C35.3209 115.653 35.6547 115.414 35.9871 115.189C34.0373 119.261 33.2386 123.87 35.337 128.127C38.0698 133.678 43.1894 135.835 48.8656 137.312C53.6649 138.562 59.6084 140.19 63.8646 136.79C64.6357 136.176 65.0763 135.577 65.2614 135.008C67.2508 133.523 67.7414 130.738 66.2745 128.665C64.9073 126.737 61.9606 125.249 59.4811 125.004C59.6446 124.814 59.7528 124.593 59.7234 124.43C58.2497 115.192 63.5776 107.401 68.2034 99.9168C69.766 97.387 71.4202 94.5109 72.6652 91.4914C72.7012 91.4859 72.722 91.4868 72.758 91.4732C73.1204 91.3686 73.4709 91.2434 73.807 91.0958C76.1574 90.0629 77.9109 87.9849 79.4513 85.8215C79.4325 86.0946 79.4067 86.3672 79.3946 86.648C79.365 87.1964 79.8959 87.2747 80.3091 87.1438C83.5009 86.1401 86.4933 84.5494 89.7879 83.9782C89.4948 84.9368 89.1935 85.8939 88.8518 86.8362C88.5254 87.7363 89.5026 87.7428 90.062 87.4784C93.6623 85.8086 97.4723 84.6896 101.217 83.4052C100.966 84.3654 100.799 85.3284 100.4 86.2823C100.242 86.6616 100.392 87.1319 100.847 87.1797C102.605 87.3382 104.371 87.4908 106.131 87.5992C105.943 87.74 105.761 87.879 105.579 88.0268C102.851 90.2035 100.735 92.9905 99.5591 96.3934C99.4234 96.7816 99.655 97.1212 100.079 97.0553C100.42 96.9927 100.695 96.9683 100.963 96.9442C94.5423 105.317 92.4176 117.181 95.0832 127.333C96.2533 131.784 98.9111 135.954 103.239 137.846C108.252 140.047 114.716 138.35 119.011 135.347C119.418 135.062 119.423 134.415 118.819 134.572C121.198 131.051 117.118 124.746 112.407 124.689C113.354 121.27 113.945 117.758 115.149 114.391C116.703 110.048 119.161 106.122 121.938 102.47C126.486 96.4671 131.723 90.499 134.885 83.5735C135.334 82.5869 135.742 81.577 136.103 80.5444C136.383 80.0711 136.629 79.5749 136.806 79.0409C136.854 78.8811 136.874 78.7551 136.857 78.649L136.956 78.64C137.03 79.2399 137.11 79.8133 137.179 80.3641C137.706 84.5282 139.309 88.7091 142.013 91.9463C142.67 92.7253 144.068 91.5963 144.279 90.916C144.418 90.4726 144.597 90.0502 144.784 89.6298C145.905 96.034 148.531 102.825 153.882 106.657C154.66 107.217 155.987 105.93 156.046 105.195C156.063 104.971 156.1 104.747 156.137 104.53C161.724 113.282 163.206 127.02 173.198 131.682C173.433 131.79 173.789 131.679 174.078 131.479C174.07 131.501 174.062 131.528 174.062 131.549C173.822 141.412 190.245 141.133 196.208 137.967C196.761 137.675 197.142 136.826 196.868 136.476C197.513 132.855 195.857 130.935 192.313 129.753C186.602 127.831 185.921 121.291 185.221 116.044C183.548 103.499 186.642 90.3191 182.191 78.3242C182.466 78.4912 182.739 78.6583 183.005 78.8447C183.565 79.2336 184.536 78.3957 184.69 77.8953C185.656 74.659 185.542 71.217 184.798 67.8306C184.811 67.8529 184.817 67.8806 184.838 67.8954C184.865 67.932 184.93 67.8783 184.932 67.8426C184.925 67.3232 184.984 66.7696 185.093 66.1973C185.335 64.95 185.827 63.6052 186.403 62.3016C186.393 62.3504 186.384 62.3982 186.383 62.4475C186.297 64.6318 187.867 66.9339 189.185 68.5084C189.701 69.1275 190.782 68.3367 191.063 67.8556C191.328 67.4028 191.6 66.9494 191.872 66.5054C193.615 71.8732 198.564 72.8907 204.252 72.4341C209.782 71.9914 215.17 70.454 220.662 69.631C222.16 70.1153 223.77 70.3027 225.017 70.2439C226.475 70.1708 227.892 69.9152 229.248 69.4443C230.125 69.1506 230.978 68.7575 231.787 68.2717C232.572 67.813 234.019 66.6997 234.381 65.5886C235.143 64.8833 235.95 63.4195 235.5 62.4084Z'
)

export function initConstellation(options: InitConstellationOptions = {}): void {
  const section = document.getElementById('constellation')
  const questionSection = document.getElementById('question')
  const canvasNode = document.getElementById('constellation-canvas') as HTMLCanvasElement | null
  const progressNode = document.getElementById('constellation-progress')
  const hintNode = document.getElementById('constellation-hint')
  const memoryNode = document.getElementById('constellation-memory')
  const completeNode = document.getElementById('constellation-complete')
  const skipNode = document.getElementById('constellation-skip') as HTMLButtonElement | null
  const uiNode = document.getElementById('constellation-ui')

  if (!section || !canvasNode || !progressNode || !hintNode || !memoryNode || !completeNode || !skipNode || !uiNode) return

  const sectionEl: HTMLElement = section
  const canvas: HTMLCanvasElement = canvasNode
  const progressEl: HTMLElement = progressNode
  const hintEl: HTMLElement = hintNode
  const memoryEl: HTMLElement = memoryNode
  const completeEl: HTMLElement = completeNode
  const skipBtn: HTMLButtonElement = skipNode
  const ui: HTMLElement = uiNode
  const kickerEl = ui.querySelector('.constellation__kicker') as HTMLElement | null

  const context = canvas.getContext('2d')
  if (!context) return
  const ctx: CanvasRenderingContext2D = context

  const isTouch = window.matchMedia('(pointer: coarse)').matches
  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT
  const hitRadius = isTouch ? MOBILE_HIT_RADIUS : DESKTOP_HIT_RADIUS

  let state: ConstellationState = 'INTRO'
  let placedCount = 0
  let started = false
  let isSectionActive = false
  let lastFrame = performance.now()
  let lastInteractionAt = performance.now()
  let memoryHideTimeout: number | null = null
  let lockFinalizeTimeout: number | null = null
  let nextShootAt = performance.now() + randomRange(9000, 18000)
  let allowShootingStars = true
  let isScrollLocked = false
  let hasUserScrollIntent = false
  let debugDragStar: SpecialStar | null = null
  let debugPointerId: number | null = null
  let shouldShowMemoryChips = true
  let spawnMinY = SPAWN_MIN_Y_DEFAULT

  const scene: SceneTweens = {
    backgroundDim: 0,
    silhouetteOpacity: IS_CONSTELLATION_DEBUG ? 0.22 : 0,
    silhouetteScale: IS_CONSTELLATION_DEBUG ? 1 : 0.96,
    constellationGlow: 1,
    lineDarken: 0,
    flash: 0,
    sceneFade: 1,
  }

  let activeTargetSlots = getTargetSlotsForViewport()
  const lineProgress = Array<number>(LINE_CONNECTIONS.length).fill(0)
  const linePreviewProgress = Array<number>(LINE_CONNECTIONS.length).fill(0)
  const lineRoseProgress = Array<number>(LINE_CONNECTIONS.length).fill(0)
  const targetById = new Map<SlotId, TargetSlot>(activeTargetSlots.map((slot) => [slot.id, slot]))
  const backgroundStars = createBackgroundStars(isMobile ? BG_STARS_MOBILE : BG_STARS_DESKTOP)
  const specialStars = createSpecialStars(
    IS_CONSTELLATION_DEBUG,
    activeTargetSlots,
    getDebugSpawnPresetFromSlots(activeTargetSlots),
    spawnMinY
  )
  const trailParticles: TrailParticle[] = []
  const placedTrailSegments: PlacedTrailSegment[] = []
  const shootingStars: ShootingStar[] = []
  const memoryChips = new Map<SlotId, MemoryChip>()

  const chipLayer = document.createElement('div')
  chipLayer.className = 'constellation__chips'
  ui.appendChild(chipLayer)

  updateProgressText()
  completeEl.classList.remove('is-visible')
  skipBtn.classList.remove('is-visible')
  memoryEl.style.display = 'none'
  questionSection?.classList.remove('is-visible')
  // Ensure no stale lock state survives into a fresh run.
  document.documentElement.classList.remove('is-scroll-locked')
  document.body.classList.remove('is-scroll-locked')
  sectionEl.classList.remove('is-locked')
  startLenis()
  if (IS_CONSTELLATION_DEBUG) {
    hintEl.textContent = 'Debug mode: drag stars to set spawn coords. Release to log.'
    logDebugSpawns(specialStars)
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0]
      isSectionActive = entry.isIntersecting && entry.intersectionRatio >= 0.45

      if (!entry.isIntersecting && isScrollLocked && state !== 'TRANSITION_OUT' && state !== 'COMPLETE') {
        unlockScroll()
      }
    },
    { threshold: [0.25, 0.45, 0.65, 0.85] }
  )
  observer.observe(sectionEl)

  const onPointerDown = (event: PointerEvent) => {
    const rect = canvas.getBoundingClientRect()
    const px = event.clientX - rect.left
    const py = event.clientY - rect.top

    if (IS_CONSTELLATION_DEBUG) {
      const nearest = getNearestStar(px, py, rect.width, rect.height, specialStars)
      if (nearest) {
        debugDragStar = nearest
        debugPointerId = event.pointerId
        canvas.setPointerCapture(event.pointerId)
        moveDebugStar(nearest, px, py, rect.width, rect.height)
      }
      return
    }

    if (state !== 'DISCOVERY' || !isSectionActive) return
    markInteraction()

    const hit = getHitStar(px, py, rect.width, rect.height, hitRadius, specialStars)
    if (!hit) return

    placeStar(hit)
  }

  const onSkip = () => {
    if (!isSectionActive) return
    markInteraction()
    if (state !== 'DISCOVERY' && state !== 'INTRO') return

    for (const star of specialStars) {
      const target = targetById.get(star.targetId)
      if (!target) continue
      star.nx = target.x
      star.ny = target.y
      star.placed = true
      star.placementIndex = placedCount
      star.assignedHue = getPlacementHue(placedCount, specialStars.length)
      star.placedAtMs = performance.now()
      star.targetTrailOpacity = 0.22
      star.glowBoost = 0.35
      addMemoryChip(memoryChips, star)
      pushPlacedTrailSegment(placedTrailSegments, star)
      placedCount += 1
    }

    hintEl.classList.add('is-hidden')
    linePreviewProgress.fill(0)
    updateProgressText()
    startCompletionSequence()
  }

  canvas.addEventListener('pointerdown', onPointerDown, { passive: true })
  canvas.addEventListener('pointermove', onPointerMove, { passive: true })
  canvas.addEventListener('pointerup', onPointerUp)
  canvas.addEventListener('pointercancel', onPointerUp)
  skipBtn.addEventListener('click', onSkip)
  window.addEventListener('resize', resizeCanvas)
  window.addEventListener('wheel', onScrollIntent, { passive: true })
  window.addEventListener('touchmove', onScrollIntent, { passive: true })
  window.addEventListener('keydown', onKeyScrollIntent)
  window.addEventListener('scroll', onWindowScroll, { passive: true })

  resizeCanvas()
  window.requestAnimationFrame(tick)

  function resizeCanvas(): void {
    applyTargetLayoutForViewport()
    const rect = canvas.getBoundingClientRect()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.floor(rect.width * dpr)
    canvas.height = Math.floor(rect.height * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    spawnMinY = IS_CONSTELLATION_DEBUG
      ? SPAWN_MIN_Y_DEFAULT
      : getSpawnMinY(canvas, [kickerEl, progressEl, hintEl])
    if (!IS_CONSTELLATION_DEBUG) {
      repositionUnplacedStarsBelowUi(specialStars, spawnMinY, activeTargetSlots)
    }
  }

  function applyTargetLayoutForViewport(): void {
    const nextSlots = getTargetSlotsForViewport()
    if (nextSlots === activeTargetSlots) return
    activeTargetSlots = nextSlots
    targetById.clear()
    for (const slot of activeTargetSlots) {
      targetById.set(slot.id, slot)
    }
  }

  function tick(now: number): void {
    const dt = Math.min((now - lastFrame) / 1000, 0.05)
    lastFrame = now

    if (!started && hasUserScrollIntent && shouldStartLockedMode(sectionEl)) {
      started = true
      lockToSection()
    }

    const width = canvas.clientWidth
    const height = canvas.clientHeight

    updateWorld(now, dt)
    renderScene(now, width, height)

    window.requestAnimationFrame(tick)
  }

  function lockToSection(): void {
    const finalizeLock = () => {
      if (lockFinalizeTimeout !== null) {
        window.clearTimeout(lockFinalizeTimeout)
        lockFinalizeTimeout = null
      }
      alignSectionToTop()
      lockScroll()
      isSectionActive = true
      if (state === 'INTRO') state = 'DISCOVERY'
    }

    const lenis = getLenis()
    if (lenis) {
      lenis.scrollTo(sectionEl, {
        duration: 0.65,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        onComplete: finalizeLock,
      })
      lockFinalizeTimeout = window.setTimeout(finalizeLock, LOCK_FALLBACK_MS)
      return
    }

    sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    lockFinalizeTimeout = window.setTimeout(finalizeLock, 320)
  }

  function lockScroll(): void {
    if (isScrollLocked) return
    isScrollLocked = true
    stopLenis()
    document.documentElement.classList.add('is-scroll-locked')
    document.body.classList.add('is-scroll-locked')
    sectionEl.classList.add('is-locked')
    window.addEventListener('wheel', preventScroll, { passive: false })
    window.addEventListener('touchmove', preventScroll, { passive: false })
    window.addEventListener('keydown', preventScrollKeys)
  }

  function unlockScroll(): void {
    if (!isScrollLocked) return
    isScrollLocked = false
    if (lockFinalizeTimeout !== null) {
      window.clearTimeout(lockFinalizeTimeout)
      lockFinalizeTimeout = null
    }
    sectionEl.classList.remove('is-locked')
    document.documentElement.classList.remove('is-scroll-locked')
    document.body.classList.remove('is-scroll-locked')
    window.removeEventListener('wheel', preventScroll)
    window.removeEventListener('touchmove', preventScroll)
    window.removeEventListener('keydown', preventScrollKeys)
    startLenis()
  }

  function updateWorld(now: number, dt: number): void {
    if (state === 'DISCOVERY' && allowShootingStars && now >= nextShootAt) {
      spawnShootingStar(shootingStars)
      nextShootAt = now + randomRange(9000, 18000)
    }

    for (let i = trailParticles.length - 1; i >= 0; i -= 1) {
      trailParticles[i].life -= dt * 2.4
      if (trailParticles[i].life <= 0) trailParticles.splice(i, 1)
    }

    for (let i = shootingStars.length - 1; i >= 0; i -= 1) {
      const star = shootingStars[i]
      star.age += dt
      star.nx += star.vnx * dt
      star.ny += star.vny * dt
      if (star.age >= star.lifetime) shootingStars.splice(i, 1)
    }

    for (let i = 0; i < linePreviewProgress.length; i += 1) {
      linePreviewProgress[i] = Math.max(0, linePreviewProgress[i] - dt * 1.9)
    }

    for (let i = placedTrailSegments.length - 1; i >= 0; i -= 1) {
      placedTrailSegments[i].alpha = Math.max(0.04, placedTrailSegments[i].alpha - dt * 0.012)
      if (placedTrailSegments[i].alpha <= 0.041 && placedTrailSegments.length > 90) {
        placedTrailSegments.splice(i, 1)
      }
    }
    if (state === 'DISCOVERY' && now - lastInteractionAt > SKIP_IDLE_MS) {
      skipBtn.classList.add('is-visible')
    }
  }

  function renderScene(now: number, width: number, height: number): void {
    ctx.clearRect(0, 0, width, height)

    ctx.save()
    ctx.globalAlpha = scene.sceneFade

    drawBackgroundGradient(ctx, width, height)
    drawBackgroundStars(ctx, now, width, height, backgroundStars, scene.backgroundDim)
    drawShootingStars(ctx, width, height, shootingStars)
    drawSilhouette(ctx, width, height, scene.silhouetteOpacity, scene.silhouetteScale, targetById)
    drawLineConnections(ctx, width, height, lineProgress, lineRoseProgress, scene.lineDarken, targetById, specialStars)
    drawLinePreview(ctx, width, height, linePreviewProgress, targetById)
    drawPlacedTrails(ctx, width, height, placedTrailSegments)
    drawTrail(ctx, width, height, trailParticles)
    drawSpecialStars(ctx, now, width, height, specialStars, scene.constellationGlow)
    updateChipLayout(chipLayer, width, height, specialStars, memoryChips, shouldShowMemoryChips)
    if (IS_CONSTELLATION_DEBUG) {
      drawDebugLabels(ctx, width, height, specialStars)
    }

    if (scene.flash > 0.001) {
      const cx = targetById.get(COLLAPSE_TARGET_ID)?.x ?? 0.38
      const cy = targetById.get(COLLAPSE_TARGET_ID)?.y ?? 0.73
      const radius = Math.max(width, height) * 0.22 * scene.flash
      const gradient = ctx.createRadialGradient(cx * width, cy * height, 0, cx * width, cy * height, radius)
      gradient.addColorStop(0, `rgba(255, 245, 215, ${0.92 * scene.flash})`)
      gradient.addColorStop(1, 'rgba(255, 245, 215, 0)')
      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(cx * width, cy * height, radius, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.restore()
  }

  function placeStar(star: SpecialStar): void {
    const target = targetById.get(star.targetId)
    if (!target) return

    markInteraction()
    skipBtn.classList.remove('is-visible')
    state = 'PLACING'
    hintEl.classList.add('is-hidden')

    star.glowBoost = 1.8
    const fromX = star.nx
    const fromY = star.ny
    const dirX = target.x - fromX
    const dirY = target.y - fromY
    const dirLength = Math.max(0.0001, Math.hypot(dirX, dirY))
    const overshoot = 0.015
    const overX = target.x + (dirX / dirLength) * overshoot
    const overY = target.y + (dirY / dirLength) * overshoot

    gsap
      .timeline({
        onComplete: () => {
          star.placed = true
          star.glowBoost = 0.42
          star.placementIndex = placedCount
          star.placedAtMs = performance.now()
          star.assignedHue = getPlacementHue(star.placementIndex, specialStars.length)
          star.targetTrailOpacity = 0.22
          placedCount += 1

          pushPlacedTrailSegment(placedTrailSegments, star)
          addMemoryChip(memoryChips, star)
          updateProgressText()
          showMemoryCaption(star.memory)

          if (placedCount >= specialStars.length) {
            startCompletionSequence()
          } else {
            state = 'DISCOVERY'
          }
        },
      })
      .to(star, {
        nx: overX,
        ny: overY,
        duration: 0.74,
        ease: 'expo.out',
        onUpdate: () => pushTrail(star),
      })
      .to(star, {
        nx: target.x,
        ny: target.y,
        duration: 0.16,
        ease: 'elastic.out(1, 0.5)',
        onUpdate: () => pushTrail(star),
      })
      .to(star, { glowBoost: 0.42, duration: 0.2, ease: 'sine.out' }, '-=0.06')
  }

  function startCompletionSequence(): void {
    if (state === 'COMPLETE' || state === 'TRANSITION_OUT') return

    state = 'COMPLETE'
    shouldShowMemoryChips = true
    allowShootingStars = false
    skipBtn.classList.remove('is-visible')
    lineProgress.fill(0)
    lineRoseProgress.fill(0)
    scene.lineDarken = 0
    gsap.set(completeEl, { opacity: 0, clipPath: 'inset(100% 0 0 0)', yPercent: 0 })

    const tl = gsap.timeline({
      delay: 0.25,
    })

    const firstPassStart = 0.42
    const firstPassStep = 0.42
    const firstPassSegmentDur = 0.58
    const firstPassEnd = firstPassStart + (LINE_CONNECTIONS.length - 1) * firstPassStep + firstPassSegmentDur
    const textStart = firstPassStart + 3 * firstPassStep + 0.1
    const chipHideStart = Math.max(0, textStart - 0.18)

    tl.to(scene, { backgroundDim: 0.5, duration: 0.8, ease: 'sine.out' }, 0)
    tl.to(scene, { constellationGlow: 1.7, duration: 1.1, ease: 'sine.inOut' }, 0.2)
    tl.to(scene, { silhouetteOpacity: 0.22, silhouetteScale: 1, duration: 1.2, ease: 'sine.out' }, 0.35)

    LINE_CONNECTIONS.forEach((_, index) => {
      tl.to(
        lineProgress,
        {
          [index]: 1,
          duration: firstPassSegmentDur,
          ease: 'power2.out',
        },
        firstPassStart + index * firstPassStep
      )
    })

    tl.call(() => {
      shouldShowMemoryChips = false
    }, [], chipHideStart)

    tl.call(() => completeEl.classList.add('is-visible'), [], textStart)
    tl.to(
      completeEl,
      {
        opacity: 1,
        clipPath: 'inset(0% 0 0 0)',
        duration: 0.75,
        ease: 'power3.out',
      },
      textStart
    )
    tl.call(() => {
      const collapseAnchor = specialStars.find((star) => star.targetId === COLLAPSE_TARGET_ID)
      if (collapseAnchor) {
        gsap.to(collapseAnchor, {
          glowBoost: 2.2,
          duration: 0.35,
          yoyo: true,
          repeat: 1,
          ease: 'sine.inOut',
        })
      }
    }, [], firstPassEnd + 0.08)
    tl.to(scene, { flash: 0.42, duration: 0.35, yoyo: true, repeat: 1, ease: 'sine.inOut' }, firstPassEnd + 0.14)
    tl.to({}, { duration: 0.56 })
    tl.call(() => startCollapse())
  }

  function startCollapse(): void {
    if (state === 'TRANSITION_OUT') return
    state = 'TRANSITION_OUT'

    const target = targetById.get(COLLAPSE_TARGET_ID)
    if (!target) return

    gsap.to(ui, { opacity: 0, duration: 0.52, ease: 'sine.out' })

    const collapseTl = gsap.timeline({
      onComplete: () => {
        unlockScroll()
        questionSection?.classList.add('is-visible')
        memoryEl.classList.remove('is-visible')
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const map = getMap()
            map?.resize()
            options.onConstellationComplete?.()
          })
        })
      },
    })

    specialStars.forEach((star) => {
      collapseTl.to(
        star,
        {
          nx: target.x,
          ny: target.y,
          duration: 0.9,
          ease: 'expo.inOut',
          onUpdate: () => pushTrail(star),
        },
        0
      )
    })

    collapseTl.to(
      scene,
      {
        backgroundDim: 0.85,
        constellationGlow: 2.3,
        flash: 1,
        sceneFade: 0,
        duration: 0.9,
        ease: 'expo.inOut',
      },
      0
    )
  }

  function showMemoryCaption(text: string): void {
    if (memoryHideTimeout) {
      window.clearTimeout(memoryHideTimeout)
      memoryHideTimeout = null
    }

    memoryEl.textContent = text
    memoryEl.classList.add('is-visible')

    memoryHideTimeout = window.setTimeout(() => {
      memoryEl.classList.remove('is-visible')
    }, MEMORY_VISIBLE_MS)
  }

  function updateProgressText(): void {
    progressEl.textContent = `Memories found: ${placedCount} / ${specialStars.length}`
  }

  function pushTrail(star: SpecialStar): void {
    trailParticles.push({
      nx: star.nx,
      ny: star.ny,
      life: 1,
      size: randomRange(1.3, 2.8),
    })
  }

  function markInteraction(): void {
    lastInteractionAt = performance.now()
  }

  function onPointerMove(event: PointerEvent): void {
    if (!IS_CONSTELLATION_DEBUG || !debugDragStar) return
    if (debugPointerId !== null && event.pointerId !== debugPointerId) return
    const rect = canvas.getBoundingClientRect()
    const px = event.clientX - rect.left
    const py = event.clientY - rect.top
    moveDebugStar(debugDragStar, px, py, rect.width, rect.height)
  }

  function onPointerUp(event: PointerEvent): void {
    if (!IS_CONSTELLATION_DEBUG || !debugDragStar) return
    if (debugPointerId !== null && event.pointerId !== debugPointerId) return

    const releasedStar = debugDragStar
    debugDragStar = null
    debugPointerId = null
    if (canvas.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId)
    }

    console.log(
      `[constellation-debug] ${releasedStar.id}: { x: ${releasedStar.nx.toFixed(3)}, y: ${releasedStar.ny.toFixed(3)} }`
    )
    logDebugSpawns(specialStars)
  }

  function moveDebugStar(star: SpecialStar, px: number, py: number, width: number, height: number): void {
    star.nx = clamp(px / width, 0, 1)
    star.ny = clamp(py / height, 0, 1)
  }

  function onScrollIntent(): void {
    hasUserScrollIntent = true
  }

  function onKeyScrollIntent(event: KeyboardEvent): void {
    const keys = ['Space', 'PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End']
    if (keys.includes(event.code)) {
      hasUserScrollIntent = true
    }
  }

  function onWindowScroll(): void {
    if (isScrollLocked && state !== 'TRANSITION_OUT') {
      alignSectionToTop()
      return
    }
  }

  function alignSectionToTop(): void {
    const targetTop = sectionEl.getBoundingClientRect().top + window.scrollY
    if (Math.abs(window.scrollY - targetTop) <= LOCK_ALIGN_TOLERANCE_PX) return
    window.scrollTo({ top: targetTop, behavior: 'auto' })
  }
}

function preventScroll(event: Event): void {
  event.preventDefault()
}

function preventScrollKeys(event: KeyboardEvent): void {
  const keys = ['Space', 'PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End']
  if (keys.includes(event.code)) {
    event.preventDefault()
  }
}

function createBackgroundStars(count: number): BackgroundStar[] {
  const stars: BackgroundStar[] = []
  for (let i = 0; i < count; i += 1) {
    stars.push({
      nx: Math.random(),
      ny: Math.random(),
      radius: randomRange(0.8, 2),
      twinkleSpeed: randomRange(0.4, 1.2),
      twinkleOffset: randomRange(0, Math.PI * 2),
    })
  }
  return stars
}

function createSpecialStars(
  useDebugPreset: boolean,
  targetSlots: TargetSlot[],
  debugPreset: Record<SlotId, { x: number; y: number }>,
  spawnMinY: number
): SpecialStar[] {
  const stars: SpecialStar[] = []
  const usedSpawns: Array<{ x: number; y: number }> = []

  for (const config of SPECIAL_STARS) {
    const spawn = useDebugPreset ? debugPreset[config.id] : getSafeSpawn(usedSpawns, targetSlots, spawnMinY)
    usedSpawns.push(spawn)
    stars.push({
      id: config.id,
      targetId: config.target,
      memory: config.memory,
      shape: SPECIAL_STAR_SHAPES_BY_ID[config.id],
      nx: spawn.x,
      ny: spawn.y,
      baseRadius: randomRange(4, 7),
      pulseOffset: randomRange(0, Math.PI * 2),
      glowBoost: randomRange(0.3, 0.45),
      placed: false,
      placementIndex: null,
      assignedHue: ORDER_HUE_START,
      placedAtMs: null,
      targetTrailOpacity: 0.16,
    })
  }

  return stars
}

function getSafeSpawn(
  used: Array<{ x: number; y: number }>,
  targetSlots: TargetSlot[],
  minY = SPAWN_MIN_Y_DEFAULT
): { x: number; y: number } {
  const minX = 0.1
  const maxX = 0.9
  const maxY = 0.86

  for (let attempt = 0; attempt < 120; attempt += 1) {
    const candidate = {
      x: randomRange(minX, maxX),
      y: randomRange(minY, maxY),
    }

    const tooClose = used.some((point) => distance(candidate.x, candidate.y, point.x, point.y) < 0.11)
    if (tooClose) continue

    const tooCloseToTarget = targetSlots.some((slot) => distance(candidate.x, candidate.y, slot.x, slot.y) < 0.08)
    if (!tooCloseToTarget) return candidate
  }

  return { x: randomRange(minX, maxX), y: randomRange(minY, maxY) }
}

function getSpawnMinY(canvas: HTMLCanvasElement, uiEls: Array<HTMLElement | null>): number {
  const rect = canvas.getBoundingClientRect()
  if (rect.height <= 0) return SPAWN_MIN_Y_DEFAULT

  let maxBottom = rect.top + rect.height * SPAWN_MIN_Y_DEFAULT
  for (const el of uiEls) {
    if (!el) continue
    const style = window.getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') continue
    const b = el.getBoundingClientRect().bottom
    if (Number.isFinite(b)) maxBottom = Math.max(maxBottom, b)
  }

  const normalized = (maxBottom - rect.top) / rect.height
  return clamp(normalized + SPAWN_TOP_PADDING_N, SPAWN_MIN_Y_DEFAULT, 0.55)
}

function repositionUnplacedStarsBelowUi(stars: SpecialStar[], minY: number, targetSlots: TargetSlot[]): void {
  const used: Array<{ x: number; y: number }> = []
  for (const star of stars) {
    if (star.placed) continue
    if (star.ny >= minY) {
      used.push({ x: star.nx, y: star.ny })
      continue
    }
    const next = getSafeSpawn(used, targetSlots, minY)
    star.nx = next.x
    star.ny = next.y
    used.push(next)
  }
}

function getDebugSpawnPresetFromSlots(slots: TargetSlot[]): Record<SlotId, { x: number; y: number }> {
  return slots.reduce<Record<SlotId, { x: number; y: number }>>((acc, slot) => {
    acc[slot.id] = { x: slot.x, y: slot.y }
    return acc
  }, {} as Record<SlotId, { x: number; y: number }>)
}

function getTargetSlotsForViewport(): TargetSlot[] {
  if (isIpadSmallLandscapeViewport()) {
    return TARGET_SLOTS_IPAD_SMALL_LANDSCAPE
  }
  if (isIpadLandscapeViewport()) {
    return TARGET_SLOTS_IPAD_LANDSCAPE
  }
  return TARGET_SLOTS_DEFAULT
}

function isIpadSmallLandscapeViewport(): boolean {
  const width = window.innerWidth
  const isLandscape = window.innerWidth > window.innerHeight
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
  return hasCoarsePointer && isLandscape && width >= IPAD_SMALL_LANDSCAPE_MIN_WIDTH && width <= IPAD_SMALL_LANDSCAPE_MAX_WIDTH
}

function isIpadLandscapeViewport(): boolean {
  const width = window.innerWidth
  const isLandscape = window.innerWidth > window.innerHeight
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
  return hasCoarsePointer && isLandscape && width >= IPAD_LANDSCAPE_MIN_WIDTH && width <= IPAD_LANDSCAPE_MAX_WIDTH
}

function isTabletLandscapeCanvas(width: number, height: number): boolean {
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
  return hasCoarsePointer && width >= 900 && width <= 1400 && width > height
}

function getHitStar(
  px: number,
  py: number,
  width: number,
  height: number,
  hitRadius: number,
  stars: SpecialStar[]
): SpecialStar | null {
  let nearest: SpecialStar | null = null
  let nearestDistance = Number.POSITIVE_INFINITY

  for (const star of stars) {
    if (star.placed) continue
    const sx = star.nx * width
    const sy = star.ny * height
    const d = Math.hypot(px - sx, py - sy)
    if (d <= hitRadius && d < nearestDistance) {
      nearest = star
      nearestDistance = d
    }
  }

  return nearest
}

function getNearestStar(px: number, py: number, width: number, height: number, stars: SpecialStar[]): SpecialStar | null {
  let nearest: SpecialStar | null = null
  let nearestDistance = Number.POSITIVE_INFINITY
  for (const star of stars) {
    const sx = star.nx * width
    const sy = star.ny * height
    const d = Math.hypot(px - sx, py - sy)
    if (d < nearestDistance) {
      nearest = star
      nearestDistance = d
    }
  }
  return nearest
}

function drawBackgroundGradient(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, '#070B1D')
  gradient.addColorStop(1, '#030510')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)
}

function drawBackgroundStars(
  ctx: CanvasRenderingContext2D,
  now: number,
  width: number,
  height: number,
  stars: BackgroundStar[],
  dimAmount: number
): void {
  const t = now * 0.001
  const alphaBase = 0.68 - dimAmount * 0.45

  for (const star of stars) {
    const twinkle = 0.45 + Math.sin(t * star.twinkleSpeed + star.twinkleOffset) * 0.35
    const alpha = clamp(alphaBase * twinkle, 0, 1)
    ctx.fillStyle = `rgba(194, 215, 255, ${alpha})`
    ctx.beginPath()
    ctx.arc(star.nx * width, star.ny * height, star.radius, 0, Math.PI * 2)
    ctx.fill()
  }
}

function drawSpecialStars(
  ctx: CanvasRenderingContext2D,
  now: number,
  width: number,
  height: number,
  stars: SpecialStar[],
  glowMultiplier: number
): void {
  const t = now * 0.001

  for (const star of stars) {
    const pulse = star.placed ? 1 : 0.77 + Math.sin(t * 1.6 + star.pulseOffset) * 0.2
    const shimmer = 0.5 + Math.sin(t * 6.6 + star.pulseOffset * 1.35) * 0.5
    const twinkleBurst = Math.pow(Math.max(0, shimmer), 1.9)
    const radius = star.baseRadius * pulse
    const x = star.nx * width
    const y = star.ny * height
    const burstBoost = star.placed ? 0.16 : twinkleBurst * 0.3
    const glow = (0.24 + star.glowBoost) * glowMultiplier * (1 + burstBoost)
    const coreColor = star.placed
      ? `hsla(${star.assignedHue}, 92%, 73%, 0.95)`
      : 'rgba(255, 224, 168, 0.95)'
    const innerColor = star.placed
      ? `hsla(${star.assignedHue}, 96%, 88%, 0.98)`
      : `rgba(255, 246, 225, ${0.88 + twinkleBurst * 0.12})`

    ctx.beginPath()
    ctx.fillStyle = star.placed
      ? `hsla(${star.assignedHue}, 95%, 68%, ${clamp(glow, 0.2, 0.96)})`
      : `rgba(255, 198, 132, ${clamp(glow, 0.18, 0.95)})`
    ctx.arc(x, y, radius * 2.45, 0, Math.PI * 2)
    ctx.fill()

    if (star.shape === 'circle') {
      ctx.beginPath()
      ctx.fillStyle = coreColor
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fill()

      ctx.beginPath()
      ctx.fillStyle = innerColor
      ctx.arc(x, y, Math.max(1.4, radius * 0.44), 0, Math.PI * 2)
      ctx.fill()
    } else {
      drawSpecialStarGlyph(ctx, x, y, radius, star.shape, twinkleBurst, star.glowBoost, star.placed, star.assignedHue)
    }
  }
}

function drawDebugLabels(ctx: CanvasRenderingContext2D, width: number, height: number, stars: SpecialStar[]): void {
  ctx.save()
  ctx.font = '12px monospace'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  const isTabletLike = isTabletLandscapeCanvas(width, height)
  const boxOffsetY = isTabletLike ? -6 : -11
  const textOffsetY = isTabletLike ? 2 : -3

  for (const star of stars) {
    const x = star.nx * width
    const y = star.ny * height
    const label = `${star.id} (${star.nx.toFixed(2)}, ${star.ny.toFixed(2)})`
    ctx.fillStyle = 'rgba(10, 14, 34, 0.74)'
    ctx.fillRect(x + 9, y + boxOffsetY, ctx.measureText(label).width + 8, 16)
    ctx.fillStyle = 'rgba(225, 238, 255, 0.95)'
    ctx.fillText(label, x + 13, y + textOffsetY)
  }

  ctx.restore()
}

function drawTrail(ctx: CanvasRenderingContext2D, width: number, height: number, trail: TrailParticle[]): void {
  for (const particle of trail) {
    ctx.beginPath()
    ctx.fillStyle = `rgba(255, 215, 163, ${particle.life * 0.5})`
    ctx.arc(particle.nx * width, particle.ny * height, particle.size * particle.life, 0, Math.PI * 2)
    ctx.fill()
  }
}

function drawLineConnections(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  progress: number[],
  roseProgress: number[],
  lineDarken: number,
  targetById: Map<SlotId, TargetSlot>,
  stars: SpecialStar[]
): void {
  ctx.lineWidth = 2.1
  ctx.lineCap = 'round'
  const starMap = new Map<SlotId, SpecialStar>(stars.map((star) => [star.id, star]))

  for (let i = 0; i < LINE_CONNECTIONS.length; i += 1) {
    const p = progress[i]
    if (p <= 0) continue

    const [fromId, toId] = LINE_CONNECTIONS[i]
    const from = targetById.get(fromId)
    const to = targetById.get(toId)
    if (!from || !to) continue

    const x1 = from.x * width
    const y1 = from.y * height
    const x2 = lerp(from.x, to.x, p) * width
    const y2 = lerp(from.y, to.y, p) * height

    const fromStar = starMap.get(fromId)
    const toStar = starMap.get(toId)
    const hue = ((fromStar?.assignedHue ?? 40) + (toStar?.assignedHue ?? 40)) * 0.5
    const allPlaced = Boolean(fromStar?.placed && toStar?.placed)
    const rose = roseProgress[i] ?? 0
    if (rose > 0) {
      ctx.strokeStyle = `rgba(170, 63, 106, ${0.68 + rose * 0.3})`
      ctx.lineWidth = 2.1 + rose * 0.9
    } else {
      ctx.lineWidth = 2.1
      if (lineDarken > 0) {
        const darkAlpha = 0.35 + lineDarken * 0.55
        ctx.strokeStyle = `rgba(28, 30, 46, ${darkAlpha})`
      } else {
        ctx.strokeStyle = allPlaced
          ? `hsla(${hue}, 88%, 76%, 0.9)`
          : 'rgba(255, 223, 169, 0.88)'
      }
    }
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  }
}

function drawLinePreview(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  previewProgress: number[],
  targetById: Map<SlotId, TargetSlot>
): void {
  ctx.lineCap = 'round'
  for (let i = 0; i < LINE_CONNECTIONS.length; i += 1) {
    const p = previewProgress[i]
    if (p <= 0) continue
    const [fromId, toId] = LINE_CONNECTIONS[i]
    const from = targetById.get(fromId)
    const to = targetById.get(toId)
    if (!from || !to) continue

    const x1 = from.x * width
    const y1 = from.y * height
    const x2 = to.x * width
    const y2 = to.y * height

    ctx.strokeStyle = `rgba(205, 231, 255, ${0.14 + p * 0.6})`
    ctx.lineWidth = 2.6 + p * 1.2
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  }
}

function drawPlacedTrails(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  segments: PlacedTrailSegment[]
): void {
  ctx.lineCap = 'round'
  for (const segment of segments) {
    ctx.strokeStyle = `hsla(${segment.hue}, 88%, 72%, ${segment.alpha})`
    ctx.lineWidth = 1.4
    ctx.beginPath()
    ctx.moveTo(segment.x1 * width, segment.y1 * height)
    ctx.lineTo(segment.x2 * width, segment.y2 * height)
    ctx.stroke()
  }
}

function drawSilhouette(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  opacity: number,
  scale: number,
  targetById: Map<SlotId, TargetSlot>
): void {
  if (opacity <= 0.001) return

  const bounds = getTargetBounds(targetById)
  const cx = width * bounds.centerX
  const cy = height * (bounds.centerY + 0.06)
  const targetW = bounds.width * width * 1.6 * scale
  const targetH = bounds.height * height * 1.7 * scale
  const svgScale = Math.min(targetW / BEAR_VIEW_W, targetH / BEAR_VIEW_H)

  ctx.save()
  ctx.globalAlpha = opacity
  ctx.translate(cx, cy)
  ctx.scale(svgScale, svgScale)
  ctx.translate(-BEAR_VIEW_W * 0.5, -BEAR_VIEW_H * 0.5)
  ctx.fillStyle = 'rgba(113, 106, 182, 0.95)'
  ctx.fill(BEAR_SILHOUETTE_PATH)
  ctx.restore()
}

function getTargetBounds(targetById: Map<SlotId, TargetSlot>): {
  minX: number
  maxX: number
  minY: number
  maxY: number
  width: number
  height: number
  centerX: number
  centerY: number
} {
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (const slot of targetById.values()) {
    minX = Math.min(minX, slot.x)
    maxX = Math.max(maxX, slot.x)
    minY = Math.min(minY, slot.y)
    maxY = Math.max(maxY, slot.y)
  }

  const width = Math.max(0.001, maxX - minX)
  const height = Math.max(0.001, maxY - minY)
  return {
    minX,
    maxX,
    minY,
    maxY,
    width,
    height,
    centerX: minX + width * 0.5,
    centerY: minY + height * 0.5,
  }
}

function spawnShootingStar(list: ShootingStar[]): void {
  const startX = randomRange(0.12, 0.92)
  const startY = randomRange(0.03, 0.26)
  const velocity = randomRange(0.35, 0.52)
  list.push({
    nx: startX,
    ny: startY,
    vnx: -velocity,
    vny: velocity * 0.62,
    age: 0,
    lifetime: 0.82,
    length: randomRange(0.06, 0.1),
  })
}

function drawShootingStars(ctx: CanvasRenderingContext2D, width: number, height: number, stars: ShootingStar[]): void {
  for (const star of stars) {
    const life = 1 - star.age / star.lifetime
    const x1 = star.nx * width
    const y1 = star.ny * height
    const x2 = (star.nx + star.length) * width
    const y2 = (star.ny - star.length * 0.55) * height

    ctx.strokeStyle = `rgba(220, 238, 255, ${life * 0.8})`
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  }
}

function drawSpecialStarGlyph(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  shape: SpecialStarShape,
  twinkleBurst: number,
  glowBoost: number,
  isPlaced: boolean,
  hue: number
): void {
  const glyph = getGlyphConfig(shape)
  if (!glyph) return

  const shimmerScale = 1 + twinkleBurst * 0.12
  const basePx = radius * glyph.baseSizeMultiplier * shimmerScale
  const scale = basePx / glyph.viewMax

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  ctx.translate(-glyph.viewW * 0.5, -glyph.viewH * 0.5)

  ctx.fillStyle = isPlaced
    ? `hsla(${hue}, 90%, 74%, ${clamp(0.72 + glowBoost * 0.35 + twinkleBurst * 0.12, 0.58, 1)})`
    : `rgba(255, 206, 118, ${clamp(0.7 + glowBoost * 0.4 + twinkleBurst * 0.25, 0.55, 1)})`
  ctx.fill(glyph.path)

  ctx.translate(glyph.viewW * 0.1, glyph.viewH * 0.08)
  ctx.scale(0.78, 0.78)
  ctx.fillStyle = isPlaced
    ? `hsla(${hue}, 96%, 90%, ${clamp(0.58 + twinkleBurst * 0.18, 0.5, 0.95)})`
    : `rgba(255, 242, 204, ${clamp(0.58 + twinkleBurst * 0.26, 0.45, 0.95)})`
  ctx.fill(glyph.path)
  ctx.restore()
}

function getGlyphConfig(shape: SpecialStarShape):
  | { path: Path2D; viewW: number; viewH: number; viewMax: number; baseSizeMultiplier: number }
  | null {
  switch (shape) {
    case 'petal40':
      return {
        path: PETAL40_PATH,
        viewW: PETAL40_VIEW_W,
        viewH: PETAL40_VIEW_H,
        viewMax: Math.max(PETAL40_VIEW_W, PETAL40_VIEW_H),
        baseSizeMultiplier: 4.7,
      }
    case 'burst57':
      return {
        path: BURST57_PATH,
        viewW: BURST57_VIEW_W,
        viewH: BURST57_VIEW_H,
        viewMax: Math.max(BURST57_VIEW_W, BURST57_VIEW_H),
        baseSizeMultiplier: 5.2,
      }
    case 'chunk74':
      return {
        path: CHUNK74_PATH,
        viewW: CHUNK74_VIEW_W,
        viewH: CHUNK74_VIEW_H,
        viewMax: Math.max(CHUNK74_VIEW_W, CHUNK74_VIEW_H),
        baseSizeMultiplier: 5.5,
      }
    default:
      return null
  }
}

function randomRange(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function distance(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function getPlacementHue(index: number, total: number): number {
  if (total <= 1) return ORDER_HUE_END
  const t = clamp(index / (total - 1), 0, 1)
  return lerp(ORDER_HUE_START, ORDER_HUE_END, t)
}

function pushPlacedTrailSegment(segments: PlacedTrailSegment[], star: SpecialStar): void {
  const jitter = 0.006
  segments.push({
    x1: clamp(star.nx + randomRange(-jitter, jitter), 0, 1),
    y1: clamp(star.ny + randomRange(-jitter, jitter), 0, 1),
    x2: clamp(star.nx + randomRange(-jitter, jitter), 0, 1),
    y2: clamp(star.ny + randomRange(-jitter, jitter), 0, 1),
    hue: star.assignedHue,
    alpha: star.targetTrailOpacity,
  })
}

function addMemoryChip(chips: Map<SlotId, MemoryChip>, star: SpecialStar): void {
  if (chips.has(star.id)) return
  chips.set(star.id, { id: star.id, text: star.memory })
}

function updateChipLayout(
  chipLayer: HTMLDivElement,
  width: number,
  height: number,
  stars: SpecialStar[],
  chips: Map<SlotId, MemoryChip>,
  shouldShowChips: boolean
): void {
  const chipElements = new Map<string, HTMLElement>()
  const children = Array.from(chipLayer.children) as HTMLElement[]
  for (const child of children) {
    const id = child.dataset.starId
    if (id) chipElements.set(id, child)
  }

  if (!shouldShowChips) {
    for (const el of chipElements.values()) {
      el.classList.remove('is-visible')
    }
    return
  }

  const placedStars = stars
    .filter((star) => star.placed)
    .sort((a, b) => (a.placementIndex ?? 0) - (b.placementIndex ?? 0))
  const visibleChipIds = new Set(placedStars.slice(-2).map((star) => star.id))

  for (let index = 0; index < placedStars.length; index += 1) {
    const star = placedStars[index]
    const chip = chips.get(star.id)
    if (!chip) continue
    let el = chipElements.get(star.id)
    if (!el) {
      el = document.createElement('div')
      el.className = 'constellation__chip'
      el.dataset.starId = star.id
      el.textContent = chip.text
      chipLayer.appendChild(el)
      chipElements.set(star.id, el)
    }

    const x = star.nx * width
    const y = star.ny * height - 12
    el.style.left = `${x}px`
    el.style.top = `${y}px`
    el.style.transform = 'translate(-50%, -100%)'
    if (visibleChipIds.has(star.id)) {
      el.classList.add('is-visible')
    } else {
      el.classList.remove('is-visible')
    }
  }

  for (const [id, el] of chipElements) {
    if (!visibleChipIds.has(id as SlotId)) {
      el.classList.remove('is-visible')
    }
  }
}

function logDebugSpawns(stars: SpecialStar[]): void {
  const output = stars.reduce<Record<string, { x: number; y: number }>>((acc, star) => {
    acc[star.id] = {
      x: Number(star.nx.toFixed(3)),
      y: Number(star.ny.toFixed(3)),
    }
    return acc
  }, {})
  console.log('[constellation-debug] current spawns', output)
}

function shouldStartLockedMode(section: HTMLElement): boolean {
  if (window.scrollY < 80) return false

  const rect = section.getBoundingClientRect()
  const vh = window.innerHeight
  const visibleTop = Math.max(rect.top, 0)
  const visibleBottom = Math.min(rect.bottom, vh)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const ratio = visibleHeight / Math.max(1, Math.min(vh, rect.height))
  const centerInside = rect.top <= vh * 0.25 && rect.bottom >= vh * 0.75

  return ratio >= 0.62 && centerInside
}
